import { useCallback, useEffect, useState } from 'react';
import { App, Button, Card, Col, Input, Row, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  TradevestOnboardingIssue,
  TradevestOnboardingOversightRow,
  TradevestOnboardingOversightSummary,
} from '@mygate/common';
import { tradevestOnboardingService } from '../services/tradevestOnboarding';
import { WAITING_ON_LABELS } from '../utils/tradevestGuidance';
import { UserDetailModal } from '../components/UserDetailModal';

const { Text } = Typography;

const ISSUE_COLORS: Record<TradevestOnboardingIssue, string> = {
  ERRORED: 'red',
  STUCK: 'orange',
  // Deliberately neutral: some drift is benign write lag, and colouring it as a failure would make
  // the tab read as 20 emergencies when most of them are not.
  DRIFT: 'default',
};

const ISSUE_LABELS: Record<TradevestOnboardingIssue, string> = {
  ERRORED: 'Errored',
  STUCK: 'Stuck',
  DRIFT: 'Drift',
};

const EMPTY_SUMMARY: TradevestOnboardingOversightSummary = { errored: 0, stuck: 0, drift: 0, total: 0 };

/** Hours are the unit the API speaks; days are the unit a four-month-old row is read in. */
function formatDuration(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${hours.toFixed(1)}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * The two error figures, each with the source it came from.
 *
 * They are deliberately not subtracted from one another. `errorCount` is the lifetime `$inc`
 * counter; `actionableErrorCount` is counted from the events array, which the logger caps at 2000 —
 * and for a customer the poll job is still writing heartbeats for, that array is a rolling window of
 * about three and a half days. The difference is therefore mostly evicted events, so presenting it
 * as "eligibility-gate rejections" reported rejections that never happened (MYG-780).
 */
function errorCounts(row: TradevestOnboardingOversightRow, message?: string) {
  return (
    <>
      {message}
      <div style={{ marginTop: 4, opacity: 0.75 }}>
        {row.errorCount} logged in total (lifetime counter); {row.actionableErrorCount} still in the log and not an
        eligibility-gate rejection
      </div>
      {row.errorsTruncated && (
        <div style={{ marginTop: 4, opacity: 0.75 }}>
          This log has passed its 2000-event cap, so older events — errors among them — are no longer in it. Open the
          customer's log for what remains.
        </div>
      )}
    </>
  );
}

type TabKey = 'ALL' | TradevestOnboardingIssue;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'ERRORED', label: 'Errored' },
  { key: 'STUCK', label: 'Silently stuck' },
  { key: 'DRIFT', label: 'Drift' },
];

/**
 * Customers whose Tradevest onboarding needs a human (MYG-770).
 *
 * The three tabs are three derived signals, not three stored flags — see
 * `tradevest-oversight.types.ts` for why neither `tradevestProvisioningStatus` nor `isFailed` can
 * answer this on its own. Read-only on purpose: retry and reset live on the per-user log page,
 * behind the timeline that gives an admin the context to decide.
 */
export default function TradevestOnboardingOversight() {
  const { message } = App.useApp();

  const [activeTab, setActiveTab] = useState<TabKey>('ALL');
  const [rows, setRows] = useState<TradevestOnboardingOversightRow[]>([]);
  const [summary, setSummary] = useState<TradevestOnboardingOversightSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [countryInput, setCountryInput] = useState('');
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });
  const [detailUserId, setDetailUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tradevestOnboardingService.listOnboardings({
        issue: activeTab === 'ALL' ? undefined : activeTab,
        country,
        page: pagination.page,
        limit: pagination.limit,
      });
      if (!response.success || !response.data) {
        message.error('Could not load onboarding issues');
        return;
      }
      setRows(response.data.items);
      setSummary(response.data.summary);
      setPagination(prev => ({ ...prev, total: response.data!.total }));
    } catch {
      message.error('Could not load onboarding issues');
    } finally {
      setLoading(false);
    }
  }, [activeTab, country, pagination.page, pagination.limit, message]);

  // Per the backoffice pagination convention: a `useEffect` watching page/limit is what actually
  // refetches — `setPagination` on its own would change the footer and leave the table stale.
  // `load` is memoised on exactly those inputs, so this fires on the first render and on each
  // filter, tab or page change, and on nothing else.
  useEffect(() => {
    void load();
  }, [load]);

  /** Filter and tab changes must land on page 1, or a page-3 filter shows an empty table. */
  const resetToFirstPage = (): void => {
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const onTabChange = (key: TabKey): void => {
    setActiveTab(key);
    resetToFirstPage();
  };

  const applyCountry = (value: string): void => {
    setCountry(value.trim() ? value.trim().toUpperCase() : undefined);
    resetToFirstPage();
  };

  const columns: ColumnsType<TradevestOnboardingOversightRow> = [
    {
      title: 'Customer',
      key: 'customer',
      width: 240,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</Text>
          <Text
            className="table-cell-truncate"
            title={row.email ?? row.phoneNumber ?? row.userId}
            type="secondary"
            style={{ fontSize: 12 }}
          >
            {row.email ?? row.phoneNumber ?? row.userId}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Land: {row.country ?? '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Tax ID',
      key: 'hasTaxId',
      width: 110,
      // The MYG-769 cohort is unblocked by exactly this, so it is worth a column of its own.
      render: (_, row) => (row.hasTaxId ? <Tag color="green">on file</Tag> : <Tag color="red">missing</Tag>),
    },
    {
      title: 'Issues',
      key: 'issues',
      width: 165,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.issues.map(issue => (
            <Tag key={issue} color={ISSUE_COLORS[issue]}>
              {ISSUE_LABELS[issue]}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Nächster Schritt',
      key: 'nextStep',
      width: 220,
      render: (_, row) =>
        row.guidance ? (
          <Space direction="vertical" size={0}>
            <Tag color={row.guidance.overdue ? 'red' : 'blue'}>{WAITING_ON_LABELS[row.guidance.waitingOn]}</Tag>
            <Text
              className="table-cell-truncate"
              title={row.guidance.explanation}
              type={row.guidance.overdue ? 'danger' : undefined}
              style={{ fontSize: 12 }}
            >
              {row.guidance.headline}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 230,
      // Both sides always, never just one: the whole cohort was invisible because staff screens
      // showed the customer record's `not_started` and nothing else.
      render: (_, row) =>
        row.hasDrift ? (
          <Space direction="vertical" size={0}>
            <Text className="table-cell-truncate" title={row.logStatus} code>
              {row.logStatus}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              customer record:{' '}
              <Text className="table-cell-truncate" title={row.userStatus ?? undefined} code>
                {row.userStatus ?? '—'}
              </Text>
            </Text>
          </Space>
        ) : (
          <Text className="table-cell-truncate" title={row.logStatus} code>
            {row.logStatus}
          </Text>
        ),
    },
    {
      title: 'Last error',
      key: 'lastError',
      width: 240,
      render: (_, row) => {
        if (row.lastErrorCode) {
          return (
            <Tooltip title={errorCounts(row, row.lastErrorMessage)}>
              <Space direction="vertical" size={0}>
                <Text className="table-cell-truncate" title={row.lastErrorCode} type="danger" style={{ fontSize: 12 }}>
                  {row.lastErrorCode}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {row.actionableErrorCount} error{row.actionableErrorCount === 1 ? '' : 's'}
                </Text>
              </Space>
            </Tooltip>
          );
        }

        // On the Errored tab with nothing to name: either the cap evicted the errors, or the events
        // that failed recorded their failure somewhere other than an `error` object (an HTTP 4xx
        // lands in `httpResponse`). Saying which beats an em dash, which on a red row reads as
        // "nothing ever went wrong here" (MYG-780). Rows that are here for another signal keep the
        // em dash, so a quiet log stays quiet.
        if (row.issues.includes('ERRORED')) {
          return (
            <Tooltip title={errorCounts(row)}>
              <Space direction="vertical" size={0}>
                <Text type="danger" style={{ fontSize: 12 }}>
                  {row.errorsTruncated ? 'details evicted' : 'no detail recorded'}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {row.errorCount} error{row.errorCount === 1 ? '' : 's'} logged
                </Text>
              </Space>
            </Tooltip>
          );
        }

        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Wartezeit',
      key: 'waiting',
      width: 150,
      render: (_, row) => (
        <div className="table-cell-stack">
          <Text type="secondary">Alter: {formatDuration(row.ageHours)}</Text>
          <Tooltip title={`Threshold for ${row.logStatus}: ${formatDuration(row.stuckThresholdHours)}`}>
            <Text type={row.issues.includes('STUCK') ? 'danger' : undefined}>
              Inaktiv: {formatDuration(row.stuckForHours)}
            </Text>
          </Tooltip>
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 200,
      // A new tab, as UserDetailModal opens it: the log page's "Fenster schließen" only works in a tab a
      // script opened (browsers ignore `window.close()` otherwise), and it keeps this tab's filter and page.
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" onClick={() => setDetailUserId(row.userId)}>
            Nutzer öffnen
          </Button>
          <Button
            size="small"
            icon={<ArrowRightOutlined />}
            onClick={() => window.open(`/tradevest-log/${row.userId}`, '_blank')}
          >
            Timeline
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Text type="secondary">Fehler und ausstehende Schritte im Onboarding prüfen.</Text>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          Refresh
        </Button>
      </Space>

      <Row className="operational-stats" gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Errored" value={summary.errored} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Silently stuck" value={summary.stuck} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Status drift" value={summary.drift} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Customers affected" value={summary.total} />
          </Card>
        </Col>
      </Row>

      <Card
        tabList={TABS.map(tab => ({ key: tab.key, tab: tab.label }))}
        activeTabKey={activeTab}
        onTabChange={key => onTabChange(key as TabKey)}
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Country (e.g. IT)"
            prefix={<SearchOutlined />}
            value={countryInput}
            onChange={e => {
              setCountryInput(e.target.value);
              // Clearing the box must clear the filter too. Otherwise the X empties the input,
              // nothing refetches, and the table keeps showing one country while looking unfiltered
              // — which reads as "these are all the broken onboardings".
              if (!e.target.value.trim()) applyCountry('');
            }}
            onPressEnter={() => applyCountry(countryInput)}
            style={{ width: 200 }}
            allowClear
          />
          <Button type="primary" onClick={() => applyCountry(countryInput)}>
            Filter
          </Button>
        </Space>

        <Table
          rowKey="userId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          tableLayout="fixed"
          scroll={{ x: 1555 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: total => `${total} customer${total === 1 ? '' : 's'}`,
            onChange: (page, pageSize) => setPagination(prev => ({ ...prev, page, limit: pageSize })),
          }}
        />
      </Card>
      <UserDetailModal
        userId={detailUserId}
        visible={detailUserId !== null}
        onClose={() => setDetailUserId(null)}
        onUserUpdated={() => void load()}
      />
    </div>
  );
}
